import React, { Suspense } from "react";

const PAGE_REFRESHED_KEY = "page-has-been-force-refreshed";

/**
 * Wraps a dynamic import yielding a React component into <Suspense> and lazy()
 *
 * @param Loader a function with a dynamic import that exports a React component
 * @param fallbackElement an element that will serve as a placeholder while the component is being imported
 */
export default function lazyLoad(
  Loader: () => Promise<{
    default: React.ComponentType<any>;
  }>,
  fallbackElement?: React.ReactNode,
) {
  const hasPageForcedRefreshed = JSON.parse(
    window.localStorage.getItem(PAGE_REFRESHED_KEY) || "false",
  );

  try {
    const Renderer = React.lazy(Loader);

    window.localStorage.setItem(PAGE_REFRESHED_KEY, "false");

    // eslint-disable-next-line react/display-name
    return <T,>(props: T) => {
      return (
        <Suspense fallback={fallbackElement || <div />}>
          <Renderer {...props} />
        </Suspense>
      );
    };
  } catch (error) {
    if (!hasPageForcedRefreshed) {
      // assume that the user is not on the latest version of the application and refresh page
      window.localStorage.setItem(PAGE_REFRESHED_KEY, "true");
      // window.location.reload has void return so cast to avoid TS error with components in routes
      return window.location.reload() as unknown as <T>(props: T) => JSX.Element;
    }
    // page already refreshed, assume that the user is on the latest version of the application
    throw error;
  }
}
